import pickBy from 'lodash/pickBy'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  coatingThicknessFormField,
  installationDateFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoSphericalPlate,
  GeckoSphericalPlateJSON,
} from 'types/assetModel/features/containmentDome/geckoSphericalPlate'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { getFormattedInstallationDateString } from 'utils/installationDateHelpers'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

export const TorisphericalHeadAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.SphericalPlate,
  GeckoFeatureModelType.Nozzle,
]

export type TorisphericalHeadAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.TorisphericalHead
  crown_radius: number // Main dome radius
  knuckle_radius: number // Transition radius between crown and straight flange
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  features: Record<string, GeckoSphericalPlateJSON | GeckoNozzleJSON>
  compass_orientation?: number
  nominal: {
    wall_thickness: number | undefined
  }
  material: string | undefined
  installation_date: string | undefined
  lining_type: string | undefined
  coating_type: string | undefined
  coating_thickness: number | undefined
  insulation_type: string | undefined
  insulation_thickness: number | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined
  tmin: number | undefined
  design_pressure: number | undefined
}

export const NewTorisphericalHeadAssetModelJSON: TorisphericalHeadAssetModelJSON =
  {
    name: 'New Torispherical Head',
    type: GeckoComponentModelType.TorisphericalHead,
    crown_radius: 0,
    knuckle_radius: 0,
    features: {},
    scene_config: {},
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: 0,
      y_end: 0,
    },
    compass_orientation: undefined,
    nominal: {
      wall_thickness: undefined,
    },
    material: undefined,
    installation_date: undefined,
    lining_type: undefined,
    coating_type: undefined,
    coating_thickness: undefined,
    insulation_type: undefined,
    insulation_thickness: undefined,
    joint_efficiency: undefined,
    stress_tolerance: undefined,
    tmin: undefined,
    design_pressure: undefined,
  }

export class GeckoTorisphericalHead extends GeckoEditableComponent {
  type: GeckoComponentModelType.TorisphericalHead
  crownRadius: GeckoMeasurement
  knuckleRadius: GeckoMeasurement
  sceneConfig = undefined
  componentBounds: GeckoComponentRect
  plates: Map<string, GeckoSphericalPlate>
  nozzles: Map<string, GeckoNozzle>
  wallNominalThickness: GeckoMeasurement | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  designPressure: GeckoMeasurement | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    crownRadius: GeckoMeasurement,
    knuckleRadius: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    plates: Map<string, GeckoSphericalPlate>,
    nozzles: Map<string, GeckoNozzle>,
    wallNominalThickness: GeckoMeasurement | undefined,
    installationDate: Date | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    designPressure: GeckoMeasurement | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.TorisphericalHead
    this.crownRadius = crownRadius
    this.knuckleRadius = knuckleRadius
    this.componentBounds = componentBounds
    this.plates = plates
    this.nozzles = nozzles
    this.wallNominalThickness = wallNominalThickness
    this.installationDate = installationDate
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.tmin = tmin
    this.designPressure = designPressure
  }

  public static CreateFromJSON(
    id: string,
    json: TorisphericalHeadAssetModelJSON
  ) {
    const name = json.name
    const crownRadius = GeckoMeasurement.CreateFromValue(
      json.crown_radius,
      GeckoMeasurementUnit.Meters
    )
    const knuckleRadius = GeckoMeasurement.CreateFromValue(
      json.knuckle_radius,
      GeckoMeasurementUnit.Meters
    )

    // Component bounds based on the crown radius (similar to hemispherical)
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      0,
      0,
      360,
      90,
      GeckoMeasurementUnit.Degrees,
      GeckoMeasurementUnit.Degrees
    )
    const childPlateJSON = pickBy(json.features, (child) => {
      return child.type === GeckoFeatureModelType.SphericalPlate
    }) as Record<string, GeckoSphericalPlateJSON>
    const plates = GeckoSphericalPlate.CreateMapFromJSON(id, childPlateJSON)

    const childNozzleJSON = pickBy(json.features, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    }) as Record<string, GeckoNozzleJSON>
    const nozzles = GeckoNozzle.CreateMapFromJSON(id, childNozzleJSON)

    const parsedWallNominalThickness = parseNumber(json.nominal.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const installationDate = parseDate(json.installation_date)
    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const designPressureJson = json.design_pressure
    const parsedDesignPressure = parseNumber(designPressureJson)
    const designPressure =
      parsedDesignPressure !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedDesignPressure,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    return new GeckoTorisphericalHead(
      id,
      json,
      name,
      crownRadius,
      knuckleRadius,
      componentBounds,
      plates,
      nozzles,
      wallNominalThickness,
      installationDate,
      jointEfficiency,
      stressTolerance,
      tmin,
      designPressure
    )
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public asJSON(): TorisphericalHeadAssetModelJSON {
    return generateTorisphericalHead(
      this.name,
      this.crownRadius,
      this.knuckleRadius,
      this.wallNominalThickness,
      this.installationDate,
      this.jointEfficiency,
      this.stressTolerance,
      this.tmin,
      this.designPressure,
      this.json as TorisphericalHeadAssetModelJSON
    )
  }
}

const generateTorisphericalHead = (
  name: string,
  crownRadius: GeckoMeasurement,
  knuckleRadius: GeckoMeasurement,
  wallNominalThickness: GeckoMeasurement | undefined,
  installationDate: Date | undefined,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  designPressure: GeckoMeasurement | undefined,
  otherData: TorisphericalHeadAssetModelJSON = NewTorisphericalHeadAssetModelJSON
): TorisphericalHeadAssetModelJSON => {
  const headCrownRadius = crownRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const headKnuckleRadius = knuckleRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // Use crown radius for circumference calculation (primary dimension)
  const headCircumference = Number(
    (2 * pi * headCrownRadius).toPrecision(assetModelPrecision)
  )

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined
  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedDesignPressure =
    designPressure !== undefined
      ? designPressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined
  return {
    name: name,
    type: GeckoComponentModelType.TorisphericalHead,
    crown_radius: headCrownRadius,
    knuckle_radius: headKnuckleRadius,
    scene_config: otherData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: headCircumference,
      y_end: headCircumference,
    },
    features: otherData.features,
    compass_orientation: otherData.compass_orientation,
    nominal: {
      wall_thickness: wallNominalThicknessMeters,
    },
    material: otherData.material,
    installation_date: installationDate
      ? getFormattedInstallationDateString(installationDate)
      : undefined,
    lining_type: otherData.lining_type,
    coating_type: otherData.coating_type,
    coating_thickness: otherData.coating_thickness,
    insulation_type: otherData.insulation_type,
    insulation_thickness: otherData.insulation_thickness,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    tmin: tminMeters,
    design_pressure: parsedDesignPressure,
  }
}

export const TorisphericalHeadAssetModelFormFields = (
  JSON: TorisphericalHeadAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Torispherical Head Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'crown_radius',
      label: 'Crown Radius',
      value: JSON.crown_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'knuckle_radius',
      label: 'Knuckle Radius',
      value: JSON.knuckle_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(JSON.nominal.wall_thickness),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: JSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: JSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tmin',
      label: 'Tmin',
      value: JSON.tmin,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'design_pressure',
      label: 'Design Pressure',
      value: JSON.design_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material Type',
      value: JSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(JSON.installation_date, true),
    {
      dataKey: 'lining_type',
      label: 'Lining Type',
      value: JSON.lining_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coating_type',
      label: 'Coating Type',
      value: JSON.coating_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    coatingThicknessFormField(JSON.coating_thickness),
    {
      dataKey: 'insulation_type',
      label: 'Insulation Type',
      value: JSON.insulation_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'insulation_thickness',
      label: 'Insulation Thickness',
      value: JSON.insulation_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const TorisphericalHeadJSONFromFormFields = (
  component: TorisphericalHeadAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const parsedDesignPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'design_pressure')?.value
  )
  const updatedComponent: TorisphericalHeadAssetModelJSON = {
    ...component,
    name: getFormFieldByDataKey(formFields, 'name')?.value as string,
    crown_radius: getFormFieldByDataKey(formFields, 'crown_radius')
      ?.value as number,
    knuckle_radius: getFormFieldByDataKey(formFields, 'knuckle_radius')
      ?.value as number,
    nominal: {
      wall_thickness: parsedWallNominalThickness,
    },
    material: getFormFieldByDataKey(formFields, 'material')?.value as
      | string
      | undefined,
    installation_date: getFormFieldByDataKey(formFields, 'installation_date')
      ?.value as string | undefined,
    lining_type: getFormFieldByDataKey(formFields, 'lining_type')?.value as
      | string
      | undefined,
    coating_type: getFormFieldByDataKey(formFields, 'coating_type')?.value as
      | string
      | undefined,
    coating_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'coating_thickness')?.value
    ),
    insulation_type: getFormFieldByDataKey(formFields, 'insulation_type')
      ?.value as string | undefined,
    insulation_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'insulation_thickness')?.value
    ),
    joint_efficiency: parsedJointEfficiency,
    stress_tolerance: parsedStressTolerance,
    tmin: parsedTmin,
    design_pressure: parsedDesignPressure,
  }

  return updatedComponent
}
